<template>
  <div class="collision-types-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$m₁v₁ + m₂v₂ = m₁v₁' + m₂v₂'$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="180" viewBox="0 0 400 180">
          <line x1="0" y1="140" x2="400" y2="140" stroke="rgba(255,255,255,0.15)" stroke-width="2" />
          
          <g :transform="`translate(${b1.x}, 140)`">
             <circle :r="b1.m * 5" fill="#3b82f6" cx="0" :cy="-b1.m * 5" />
             <text :y="-b1.m * 5 - 12" text-anchor="middle" fill="#94a3b8" font-weight="700" font-size="11">m₁={{ b1.m }}kg</text>
             <line x1="0" :y1="-b1.m * 5" :x2="b1.v * 10" :y2="-b1.m * 5" stroke="#fff" stroke-width="1.5" marker-end="url(#arr-v)" />
          </g>

          <g :transform="`translate(${b2.x}, 140)`">
             <circle :r="b2.m * 5" fill="#ef4444" cx="0" :cy="-b2.m * 5" />
             <text :y="-b2.m * 5 - 12" text-anchor="middle" fill="#94a3b8" font-weight="700" font-size="11">m₂={{ b2.m }}kg</text>
             <line x1="0" :y1="-b2.m * 5" :x2="b2.v * 10" :y2="-b2.m * 5" stroke="#fff" stroke-width="1.5" marker-end="url(#arr-v)" />
          </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item blue">
              <label>系统总动量 P</label>
              <span class="val">{{ totalMomentum.toFixed(1) }} <small>kg·m/s</small></span>
           </div>
           <div class="hud-item green">
              <label>系统总动能 Ek</label>
              <span class="val">{{ totalKinetic.toFixed(1) }} <small>J</small></span>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
          <div class="control-vertical-group">
             <!-- 行 1: 模式切换 -->
             <div class="mode-selector-row">
                <span class="label">碰撞模式：</span>
                <el-radio-group v-model="collisionType" size="small" class="full-row-radio">
                   <el-radio-button label="elastic">完全弹性 (Ek守恒)</el-radio-button>
                   <el-radio-button label="inelastic">完全非弹性 (粘连)</el-radio-button>
                </el-radio-group>
             </div>
             
             <!-- 行 2: 动作按钮 -->
             <div class="action-row">
                <el-button type="primary" size="small" @click="play" icon="el-icon-video-play" :disabled="running">开始碰撞演示</el-button>
                <el-button size="small" plain @click="reset" icon="el-icon-refresh-right">重置实验</el-button>
             </div>
          </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>物理规律：</strong>碰撞中动量始终守恒。<strong>弹性碰撞</strong>机械能不损失；<strong>极硬非弹性碰撞</strong>合为一体，机械能损失最大。</p>
        </div>
      </div>
    </div>

    <defs>
       <marker id="arr-v" markerWidth="6" markerHeight="4" refX="6" refY="2" orient="auto">
          <polygon points="0 0, 6 2, 0 4" fill="#fff" />
       </marker>
    </defs>
  </div>
</template>

<script>
export default {
  name: 'CollisionTypesViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      collisionType: 'elastic',
      b1: { x: 50, v: 4, m: 2.5 },
      b2: { x: 250, v: 0, m: 5 },
      running: false,
      timer: null
    }
  },
  computed: {
    totalMomentum() { return this.b1.m * this.b1.v + this.b2.m * this.b2.v; },
    totalKinetic() { return 0.5 * this.b1.m * this.b1.v**2 + 0.5 * this.b2.m * this.b2.v**2; }
  },
  methods: {
    play() {
      if (this.running) return;
      this.running = true;
      this.update();
    },
    reset() {
      this.running = false;
      if (this.frameId) cancelAnimationFrame(this.frameId);
      this.frameId = null;
      this.b1 = { x: 50, v: 4, m: 2.5 };
      this.b2 = { x: 250, v: 0, m: 5 };
    },
    update() {
      if (!this.running) return;
      this.b1.x += this.b1.v;
      this.b2.x += this.b2.v;
      let r1 = this.b1.m * 5;
      let r2 = this.b2.m * 5;
      if (this.b1.x + r1 >= this.b2.x - r2) {
        if (this.collisionType === 'elastic') {
           let v1 = ((this.b1.m - this.b2.m) * this.b1.v + 2 * this.b2.m * this.b2.v) / (this.b1.m + this.b2.m);
           let v2 = ((this.b2.m - this.b1.m) * this.b2.v + 2 * this.b1.m * this.b1.v) / (this.b1.m + this.b2.m);
           this.b1.v = v1;
           this.b2.v = v2;
        } else {
           let commonV = (this.b1.m * this.b1.v + this.b2.m * this.b2.v) / (this.b1.m + this.b2.m);
           this.b1.v = commonV;
           this.b2.v = commonV;
           this.b1.x = this.b2.x - r1 - r2;
        }
        this.b1.x -= 2; this.b2.x += 2;
      }
      if (this.b1.x < 0 || this.b2.x > 400) {
        this.running = false;
        if (this.frameId) cancelAnimationFrame(this.frameId);
        return;
      }
      this.frameId = requestAnimationFrame(this.update);
    }
  },
  beforeDestroy() {
     this.running = false;
     if (this.frameId) cancelAnimationFrame(this.frameId);
  }
}
</script>

<style scoped lang="scss">
.collision-types-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 200px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f1f5f9; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.blue { border-left: 3px solid #3b82f6; } &.green { border-left: 3px solid #10b981; } } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-vertical-group { display: flex; flex-direction: column; gap: 10px; .mode-selector-row { display: flex; align-items: center; gap: 10px; .label { font-size: 12px; color: #64748b; font-weight: 700; white-space: nowrap; } .full-row-radio { flex: 1; display: flex; flex-wrap: nowrap; } } .action-row { display: flex; gap: 8px; border-top: 1px solid #f1f5f9; padding-top: 10px; .el-button { flex: 1; font-weight: 700; } } }
.discovery-card-compact { background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: center; gap: 10px; i { color: #0ea5e9; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 12px; color: #0369a1; line-height: 1.5; strong { color: #075985; } } }
</style>
